import React from 'react';
import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import Avatar from '../../../../../avatar/Avatar';
import {
  ContentTooltiptStudentLevelMarkingInfo,
  ContentTooltiptLevelInfo,
  useStyles
} from '../ContentTooltip/ContentTooltip';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import AvatarSkeleton from '../../../../../avatar/AvatarSkeleton';

const TooltipLevel = ({ marking, student, children, id = '' }) => {
  const classes = useStyles();
  if (!marking) return <>{children}</>;
  return (
    <Tooltip
      id={`tooltip-marking-${student.studentId}-${marking?.markingType}-${id}`}
      arrow
      classes={{ tooltip: classes.customWidth }}
      title={
        <ContentTooltiptStudentLevelMarkingInfo
          level={{
            name: marking?.levelName,
            definition: marking?.definition
          }}
          student={student}
          marking={marking || {}}
        />
      }
    >
      {children}
    </Tooltip>
  );
};

const RedenLevel = ({ level, student, marking, height, children }) => {
  return (
    <>
      {level.position <= marking?.levelPosition ? (
        <TooltipLevel id='render' marking={marking} student={student}>
          <div
            className='text-xs flex flex-col items-center justify-center w-full cursor-pointer'
            style={{
              height,
              backgroundColor: marking?.markingPrimaryColor
            }}
          >
            {children}
          </div>
        </TooltipLevel>
      ) : (
        <div
          className='text-xs flex flex-col items-center justify-center w-full'
          style={{
            height,
            backgroundColor: 'rgba(0,0,0,0.03)'
          }}
        >
          {children}
        </div>
      )}
    </>
  );
};

const ComparisonMarkingContainer = ({ isLoading, datasets = [], onStudentView }) => {
  const levels = datasets?.levels?.sort((a, b) => a.position - b.position) || [];
  const data = datasets?.markingComparison?.sort((a, b) => b.completed - a.completed) || [];
  const len = levels.length;
  const height = 45;
  const classes = useStyles();

  return (
    <div className='relative h-full'>
      <Box className={'absolute inset-0 overflow-auto'}>
        <Typography
          color='primary'
          variant='subtitle1'
          className='sticky top-0px left-0px z-20 bg-background h-8 text-center'
        >
          Marking comparison
        </Typography>
        <Table>
          <TableBody>
            {isLoading ? (
              Array(10)
                .fill()
                .map((_, index) => (
                  <TableRow key={`marking-skeleton-${index}`}>
                    <TableCell
                      className={[
                        'min-w-150px max-w-150px  sticky left-0px border-r border-muted z-1',
                        index % 2 ? 'bg-background-foreground' : 'bg-background'
                      ].join(' ')}
                    >
                      <div className='flex items-center justify-end gap-2 py-1 '>
                        <Typography>
                          <Skeleton width='100px' />
                        </Typography>
                        <div style={{ marginRight: -25 }}>
                          <AvatarSkeleton size='small' variant='circular' />
                        </div>
                      </div>
                    </TableCell>
                    {Array(5)
                      .fill()
                      .map((_, il) => (
                        <TableCell
                          key={`marking-loading-${index}-level-index-${il}`}
                          className='border-l border-muted '
                          style={{
                            width: `${100 / 5}%`,
                            padding: 0
                          }}
                        >
                          <div className='flex flex-col py-2 items-center justify-center gap-1'>
                            <Skeleton width={'100%'} height={height / 2} variant='rectangular' />
                            <Skeleton width={'100%'} height={height / 2} variant='rectangular' />
                          </div>
                        </TableCell>
                      ))}
                  </TableRow>
                ))
            ) : (
              <>
                {data?.map((student, index) => {
                  return (
                    <TableRow key={`marking-${student.studentId}-index-${index}`}>
                      <TableCell
                        className={[
                          'min-w-150px max-w-150px  sticky left-0px border-r border-muted z-2 relative',
                          index % 2 ? 'bg-background-foreground' : 'bg-background'
                        ].join(' ')}
                      >
                        <div className='flex items-center justify-end gap-2 py-1 cursor-pointer' onClick={() => onStudentView(student)}>
                          <Tooltip arrow title={`${student?.code} - ${student?.fullName}`}>
                            <Typography className='text-ellipsis text-nowrap pl-4 '>
                              {student?.fullName}
                            </Typography>
                          </Tooltip>
                          <div style={{ marginRight: -25 }}>
                            <Avatar alt={student.code} src={`photos/${student.photo}`} size='small' />
                          </div>
                        </div>
                        <div
                          className='absolute z-10 top-0 right-0'
                          style={{
                            left: 'calc(100% + 5rem)'
                          }}
                        >
                          <div className='flex flex-col py-2 items-center justify-center gap-1 text-muted-foreground opacity-50'>
                            <div style={{ height: height / 2 }} className='flex flex-row gap-2'  onClick={() => onStudentView(student, 'completedTeacher')}>
                              <TooltipLevel marking={student?.completedTeacher} student={student}>
                                <Typography className='text-ellipsis text-nowrap pl-4 cursor-pointer text-clickeabled'>
                                  By teacher
                                </Typography>
                              </TooltipLevel>
                            </div>
                            <div style={{ height: height / 2 }} className='flex flex-row gap-2'  onClick={() => onStudentView(student, 'completedStudent')}>
                              <TooltipLevel marking={student?.completedStudent} student={student}>
                                <Typography className='text-ellipsis text-nowrap pl-4 cursor-pointer text-clickeabled'>
                                  By student
                                </Typography>
                              </TooltipLevel>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      {levels.map((level, il) => (
                        <TableCell
                          key={`marking-${student.studentId}-level-${level._id}-index-${il}`}
                          className='border-l border-muted relative'
                          style={{
                            padding: 0
                          }}
                        >
                          <div className='flex flex-col py-2 items-center justify-center gap-1'>
                            <RedenLevel
                              level={level}
                              marking={student?.completedTeacher}
                              height={height / 2}
                              student={student}
                            />
                            <RedenLevel
                              level={level}
                              marking={student?.completedStudent}
                              height={height / 2}
                              student={student}
                            />
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell
                    className={['min-w-150px max-w-150px sticky left-0px bottom-0 z-10 bg-background'].join(
                      ' '
                    )}
                    style={{
                      height: height / 2,
                      border: 'none',
                      padding: 0
                    }}
                  >
                    <div
                      className='flex items-center justify-center w-full gap-2 py-1'
                      style={{
                        outline: `1px solid rgb(206, 206, 206)`,
                        height: height / 2,
                        border: 'none'
                      }}
                    ></div>
                  </TableCell>
                  {levels.map((level, il) => (
                    <TableCell
                      key={`level-${level._id}-index-${il}`}
                      className='sticky bottom-0 z-2 bg-background'
                      style={{
                        minWidth: `calc(30px + 1px)`,
                        width: `${100 / len}%`,
                        height: height / 2,
                        border: 'none',
                        padding: 0
                      }}
                    >
                      <Tooltip
                        arrow
                        classes={{ tooltip: classes.customWidth }}
                        title={<ContentTooltiptLevelInfo level={level} />}
                      >
                        <div
                          className='flex items-center justify-center w-full text-sm cursor-pointer  gap-2 py-1'
                          style={{
                            minWidth: `calc(20px + 1px)`,
                            outline: `1px solid rgb(206, 206, 206)`,
                            height: height / 2,
                            border: 'none'
                          }}
                        >
                          {level.shortName}
                        </div>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </Box>
    </div>
  );
};

export default ComparisonMarkingContainer;
